import type { Command } from "@/commands/Command";
import {
  createDuplicateNodeCommands,
  DeleteNodeCommand,
  InsertNodeCommand,
  MoveNodesInTreeCommand,
  UpdateNodeStyleCommand,
} from "@/commands/nodeCommands";
import {
  InsertAnimationNodeCommand,
  UpsertAnimationConnectionCommand,
} from "@/commands/animationCommands";
import { InsertVectorSelectionCommand } from "@/commands/vectorCommands";
import { TransactionCommand } from "@/commands/transaction";
import type { AnimationConnection, AnimationGraph, AnimationGraphNode, AnimationTarget } from "@/document/model/animation";
import { createEmptyAnimationGraph, normalizeAnimationGraph } from "@/document/model/animation";
import {
  createPrimitiveNode,
  createTextNode,
  findSceneNode,
  getActivePage,
  getAllSceneNodes,
  getSceneNodeChildren,
  resolveActivePageId,
  type CurvaDocument,
} from "@/document/model/document";
import {
  createIdentityTransform,
  type SceneNode,
  type VectorNetworkNode,
} from "@/document/model/nodes";
import type { VectorEdge, VectorRegion, VectorVertex } from "@/document/model/vectorNetwork";
import { createDefaultStyle, type NodeStyle, type Paint } from "@/document/model/styles";
import { editableNodeIds, isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import { getVectorNetworkBounds } from "@/document/operations/vectorGeometry";
import { clipboardStore, documentStore, selectionStore, uiStore, viewStore, type VectorSelectionClipboard } from "@/editor/store";
import { frameDropTargetForMovedNodes } from "@/editor/shell/canvasDropTargets";
import { importSvgMarkup } from "@/import/svg/svgImporter";
import { createId } from "@/lib/ids";
import type { Rect } from "@/lib/math/rect";
import { inverseRotatePoint, rectCenter, transformRotationOrigin } from "@/lib/math/rotation";
import {
  DEFAULT_DUPLICATE_OFFSET,
  childrenCountForParent,
  cloneNodeForPaste,
  createObjectClipboardPayload,
  createStyleClipboardPayload,
  duplicateOffsetForSelection,
  imageRectangleBoundsAtPoint,
  pasteParentIdForNode,
  buildStylePasteCommands,
  readImageClipboard,
  readImageClipboardFromDataTransfer,
  readAnimationClipboard,
  readObjectClipboard,
  readPlainTextClipboard,
  readSvgClipboard,
  readSvgFromDataTransfer,
  readStyleClipboard,
  registerClipboardAssetsForDocument,
  selectedPasteContainerId,
  topLevelSelectedNodes,
  writeAnimationClipboard,
  writeObjectClipboard,
  writeStyleClipboard,
  type CurvaAnimationClipboardPayload,
  type CurvaClipboardPayload,
  type CurvaStyleClipboardPayload,
  type StyleClipboardPart,
} from "@/editor/shell/shellTransfer";
import { textBoundsForLayout } from "@/editor/shell/shellTextHelpers";
import type { EditorCommandHost } from "../editorCommandHost";
import { editableSelection } from "./selectionHandlers";
import { deleteSelectedVectorSelection } from "./vectorHandlers";
import { placeNodeInParent } from "./structureHandlers";

function resolvedActivePageId(): string | undefined {
  const document = documentStore.getDocument();
  return resolveActivePageId(document, selectionStore.getSelection().activePageId);
}

function currentAnimationGraph(): AnimationGraph {
  const document = documentStore.getDocument();
  return normalizeAnimationGraph(document.animation ?? createEmptyAnimationGraph());
}

export function resetDuplicateContinuation(): void {
  clipboardStore.resetDuplicateContinuation();
}

export function insertVectorSelection(
  host: EditorCommandHost,
  nodeId: string,
  payload: VectorSelectionClipboard,
  offset: { x: number; y: number },
  _label: string,
  activeSourceVertexId = payload.vertices.at(-1)?.id,
): { document: CurvaDocument; vertexIds: string[]; activeVertexId?: string; vertices: Array<{ vertexId: string; x: number; y: number }> } | undefined {
  const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork" || payload.vertices.length === 0) {
    return undefined;
  }

  const vertexIdMap = new Map(payload.vertices.map(vertex => [vertex.id, createId("vertex")]));
  const edgeIdMap = new Map(payload.edges.map(edge => [edge.id, createId("edge")]));
  const insertedVertices = payload.vertices.map(vertex => ({
    ...structuredClone(vertex),
    id: vertexIdMap.get(vertex.id)!,
    x: vertex.x + offset.x,
    y: vertex.y + offset.y,
  }));
  const insertedEdges = payload.edges.flatMap(edge => {
    const fromVertexId = vertexIdMap.get(edge.from.vertexId);
    const toVertexId = vertexIdMap.get(edge.to.vertexId);
    const edgeId = edgeIdMap.get(edge.id);
    return fromVertexId && toVertexId && edgeId
      ? [{
          ...structuredClone(edge),
          id: edgeId,
          from: { ...structuredClone(edge.from), vertexId: fromVertexId },
          to: { ...structuredClone(edge.to), vertexId: toVertexId },
        }]
      : [];
  });
  const insertedRegions = payload.regions.flatMap(region => {
    const edgeIds = region.edgeIds.map(edgeId => edgeIdMap.get(edgeId));
    return edgeIds.every(Boolean)
      ? [{ ...structuredClone(region), id: createId("region"), edgeIds: edgeIds as string[] }]
      : [];
  });
  const insertedVertexIds = insertedVertices.map(vertex => vertex.id);
  const activeVertexId = activeSourceVertexId ? vertexIdMap.get(activeSourceVertexId) : insertedVertexIds.at(-1);
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [nodeId],
    primarySelectedNodeId: nodeId,
    mode: "vectorEdit",
    selectedVertexId: activeVertexId,
    selectedVertexIds: insertedVertexIds,
    selectedEdgeIds: [],
    selectedHandle: undefined,
  });
  const context = host.commandContext();
  host.history.execute(
    new InsertVectorSelectionCommand(nodeId, insertedVertices, insertedEdges, insertedRegions),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  resetDuplicateContinuation();
  return {
    document: context.document,
    vertexIds: insertedVertexIds,
    activeVertexId,
    vertices: insertedVertices.map(vertex => ({ vertexId: vertex.id, x: vertex.x, y: vertex.y })),
  };
}

export function copySelectedVectorVertices(host: EditorCommandHost): boolean {
  const selection = selectionStore.getSelection();
  if (selection.mode !== "vectorEdit" || !selection.primarySelectedNodeId || (selection.selectedVertexIds ?? []).length === 0) {
    return false;
  }

  const payload = vectorSelectionPayload(documentStore.getDocument(), selection.primarySelectedNodeId, selection.selectedVertexIds ?? []);
  if (!payload) {
    uiStore.setMessage("Nothing to copy");
    return true;
  }

  clipboardStore.localVectorClipboard = payload;
  uiStore.setMessage(payload.vertices.length > 1 ? `Copied ${payload.vertices.length} vertices` : "Copied vertex");
  return true;
}

export function canPasteVectorVertices(): boolean {
  const selection = selectionStore.getSelection();
  return selection.mode === "vectorEdit" && Boolean(selection.primarySelectedNodeId && clipboardStore.localVectorClipboard);
}

export function pasteVectorVerticesFromClipboard(host: EditorCommandHost): boolean {
  if (!canPasteVectorVertices()) {
    return false;
  }

  const selection = selectionStore.getSelection();
  const payload = clipboardStore.localVectorClipboard!;
  const nodeId = selection.primarySelectedNodeId!;
  const viewportBounds = viewStore.viewportBounds.getValue();
  const viewportCenter = viewStore.viewportCenter.getValue() ?? rectCenter(payload.bounds);
  const sourceVisible = viewportBounds ? rectsIntersect(payload.bounds, viewportBounds) : true;
  const localViewportCenter = vectorEditLocalPointForNode(documentStore.getDocument(), nodeId, viewportCenter);
  const offset = sourceVisible
    ? { x: 0, y: 0 }
    : {
        x: localViewportCenter.x - (payload.bounds.x + payload.bounds.width / 2),
        y: localViewportCenter.y - (payload.bounds.y + payload.bounds.height / 2),
      };
  const inserted = insertVectorSelection(host, nodeId, payload, offset, "Paste Vector Selection");
  if (!inserted) {
    uiStore.setMessage("Nothing to paste");
    return true;
  }

  host.commit(inserted.document, inserted.vertexIds.length > 1 ? `Pasted ${inserted.vertexIds.length} vertices` : "Pasted vertex");
  return true;
}

export function pasteVectorSelectionAsObjectFromClipboard(host: EditorCommandHost): boolean {
  const selection = selectionStore.getSelection();
  const payload = clipboardStore.localVectorClipboard;
  if (selection.mode === "vectorEdit" || !payload) {
    return false;
  }

  const context = host.commandContext();
  const activePageId = resolvedActivePageId();
  const viewportBounds = viewStore.viewportBounds.getValue();
  const viewportCenter = viewStore.viewportCenter.getValue() ?? rectCenter(payload.bounds);
  const selectedTargetParentId = selectedPasteContainerId(context.document, selection.selectedNodeIds, selection.primarySelectedNodeId);
  const targetFrameId = selectedTargetParentId ? undefined : topLevelFrameIdAtPoint(context.document, viewportCenter, activePageId);
  const parentId = selectedTargetParentId ?? targetFrameId ?? activePageId;
  if (!parentId) {
    uiStore.setMessage("No paste target available");
    return true;
  }

  const sourceVisible = viewportBounds ? rectsIntersect(payload.bounds, viewportBounds) : true;
  const offset = sourceVisible
    ? { x: 0, y: 0 }
    : {
        x: viewportCenter.x - (payload.bounds.x + payload.bounds.width / 2),
        y: viewportCenter.y - (payload.bounds.y + payload.bounds.height / 2),
      };
  const sourceNode = getAllSceneNodes(context.document).find(node => node.id === payload.nodeId);
  const node = vectorSelectionPayloadToNode(payload, offset, sourceNode?.style);
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [node.id],
    primarySelectedNodeId: node.id,
    mode: "select",
    selectedVertexId: undefined,
    selectedVertexIds: [],
    selectedEdgeIds: [],
    selectedHandle: undefined,
  });
  host.history.execute(
    new InsertNodeCommand(parentId, node, childrenCountForParent(context.document, parentId)),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  resetDuplicateContinuation();
  host.commit(context.document, payload.vertices.length > 1 ? `Pasted ${payload.vertices.length} vertices as object` : "Pasted vertex as object");
  return true;
}

export function duplicateVectorVerticesForDrag(
  host: EditorCommandHost,
  nodeId: string,
  vertexIds: string[],
  activeVertexId: string,
): { vertexIds: string[]; activeVertexId?: string; vertices: Array<{ vertexId: string; x: number; y: number }> } | undefined {
  const payload = vectorSelectionPayload(documentStore.getDocument(), nodeId, vertexIds);
  if (!payload) {
    return undefined;
  }

  const inserted = insertVectorSelection(host, nodeId, payload, { x: 0, y: 0 }, "Alt Drag Duplicate Vertices", activeVertexId);
  if (!inserted) {
    return undefined;
  }

  host.commit(inserted.document, inserted.vertexIds.length > 1 ? `Duplicated ${inserted.vertexIds.length} vertices` : "Duplicated vertex");
  return inserted;
}

export async function cutSelectedObjects(host: EditorCommandHost): Promise<void> {
  const selection = selectionStore.getSelection();
  if (selection.mode === "textEdit") {
    return;
  }

  if (selection.mode === "vectorEdit") {
    const edgeCount = selection.selectedEdgeIds?.length ?? 0;
    const vertexCount = (selection.selectedVertexIds?.length ?? 0) || (selection.selectedVertexId ? 1 : 0);
    if (!copySelectedVectorVertices(host)) {
      uiStore.setMessage("Nothing to cut");
      return;
    }

    if (!deleteSelectedVectorSelection(host)) {
      return;
    }

    if (edgeCount > 0) {
      uiStore.setMessage(edgeCount > 1 ? "Cut edges" : "Cut edge");
    } else if (vertexCount > 0) {
      uiStore.setMessage(vertexCount > 1 ? `Cut ${vertexCount} vertices` : "Cut vertex");
    }
    return;
  }

  const nodes = topLevelSelectedNodes(documentStore.getDocument(), selection.selectedNodeIds);
  if (nodes.length === 0) {
    uiStore.setMessage("Nothing to cut");
    return;
  }

  await copySelectedObjects(host);
  deleteNodes(host, editableSelection());
  uiStore.setMessage(nodes.length > 1 ? `Cut ${nodes.length} objects` : `Cut ${nodes[0]!.name}`);
}

export async function copySelectedObjects(host: EditorCommandHost): Promise<void> {
  const selection = selectionStore.getSelection();
  if (selection.mode === "textEdit") {
    return;
  }

  if (copySelectedVectorVertices(host)) {
    return;
  }

  const nodes = topLevelSelectedNodes(documentStore.getDocument(), selection.selectedNodeIds);
  if (nodes.length === 0) {
    uiStore.setMessage("Nothing to copy");
    return;
  }

  const payload = createObjectClipboardPayload(documentStore.getDocument(), nodes);
  clipboardStore.localClipboard = payload;
  clipboardStore.localStyleClipboard = undefined;
  clipboardStore.localVectorClipboard = undefined;

  try {
    await writeObjectClipboard(payload);
    uiStore.setMessage(nodes.length > 1 ? `Copied ${nodes.length} objects` : `Copied ${nodes[0]!.name}`);
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Copy failed");
  }
}

export async function copyPrimarySelectionStyle(host: EditorCommandHost, parts: StyleClipboardPart[]): Promise<void> {
  const selection = selectionStore.getSelection();
  const nodeId = selection.primarySelectedNodeId ?? selection.selectedNodeIds.at(-1);
  if (!nodeId) {
    uiStore.setMessage("Nothing to copy");
    return;
  }

  await copyNodeStyle(host, nodeId, parts);
}

export async function copyNodeStyle(
  host: EditorCommandHost,
  nodeId: string,
  parts: StyleClipboardPart[],
): Promise<void> {
  const node = findSceneNode(documentStore.getDocument(), nodeId);
  if (!node) {
    uiStore.setMessage("Nothing to copy");
    uiStore.setContextMenu(undefined);
    return;
  }

  const payload = createStyleClipboardPayload(documentStore.getDocument(), node, parts);
  clipboardStore.localStyleClipboard = payload;
  clipboardStore.localClipboard = undefined;
  clipboardStore.localVectorClipboard = undefined;

  try {
    await writeStyleClipboard(payload);
    uiStore.setMessage(styleCopyMessage(payload, node.name));
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Copy failed");
  } finally {
    uiStore.setContextMenu(undefined);
  }
}

export async function pasteStyleToSelection(host: EditorCommandHost, nodeIds?: string[]): Promise<void> {
  const selection = selectionStore.getSelection();
  if (selection.mode === "textEdit") {
    return;
  }

  const targetNodeIds = nodeIds ?? selection.selectedNodeIds;
  const payload = await readStyleClipboard(clipboardStore.localStyleClipboard);
  if (!payload || payload.parts.length === 0) {
    uiStore.setMessage("Clipboard has no style");
    uiStore.setContextMenu(undefined);
    return;
  }

  const context = host.commandContext();
  const targetIds = editableNodeIds(context.document, targetNodeIds);
  const nodes = getAllSceneNodes(context.document).filter(node => targetIds.includes(node.id));
  const commands: Command[] = [];

  for (const node of nodes) {
    commands.push(...buildStylePasteCommands(context.document, node, payload));
  }

  if (commands.length === 0) {
    uiStore.setMessage(nodes.length > 0 ? "Style already matches" : "No editable style target");
    uiStore.setContextMenu(undefined);
    return;
  }

  host.history.execute(
    commands.length === 1 ? commands[0]! : new TransactionCommand("Paste Style", commands),
    context,
    { afterSelection: host.currentSelection() },
  );
  host.commit(context.document, nodes.length > 1 ? `Pasted style to ${nodes.length} objects` : "Pasted style");
  uiStore.setContextMenu(undefined);
}

export async function pasteObjects(host: EditorCommandHost): Promise<void> {
  const selection = selectionStore.getSelection();
  if (selection.mode === "textEdit") {
    return;
  }

  if (pasteVectorVerticesFromClipboard(host)) {
    return;
  }

  if (pasteVectorSelectionAsObjectFromClipboard(host)) {
    return;
  }

  await new Promise(resolve => window.setTimeout(resolve, 0));
  if (performance.now() - clipboardStore.imagePasteHandledAt < 250) {
    return;
  }

  const svgMarkup = await readSvgClipboard();
  if (svgMarkup) {
    pasteSvg(host, svgMarkup, undefined);
    return;
  }

  const imagePaint = await readImageClipboard();
  if (imagePaint) {
    pasteImage(host, imagePaint, undefined);
    return;
  }

  const plainText = await readPlainTextClipboard();
  const payload = await readObjectClipboard(plainText ? undefined : clipboardStore.localClipboard);
  if (payload && payload.nodes.length > 0) {
    pasteObjectPayload(host, payload);
    return;
  }

  if (plainText) {
    if (plainText.trim().startsWith("<svg") || plainText.includes("<svg")) {
      pasteSvg(host, plainText, undefined);
      return;
    }
    pasteText(host, plainText);
    return;
  }

  uiStore.setMessage("Clipboard has no supported paste content");
}

export function pasteObjectPayload(host: EditorCommandHost, payload: CurvaClipboardPayload): void {
  if (!payload || payload.nodes.length === 0) {
    return;
  }

  const selection = selectionStore.getSelection();
  const context = host.commandContext();
  const activePageId = resolvedActivePageId();
  registerClipboardAssetsForDocument(context.document, payload);
  const commands: InsertNodeCommand[] = [];
  const pastedNodeIds: string[] = [];
  const insertionCounts = new Map<string, number>();
  const viewportCenter = viewStore.viewportCenter.getValue() ?? { x: 0, y: 0 };
  const selectedTargetParentId = selectedPasteContainerId(context.document, selection.selectedNodeIds, selection.primarySelectedNodeId);
  const explicitTargetParentId = selectedTargetParentId && !payload.nodes.some(node => nodeContainsId(node, selectedTargetParentId))
    ? selectedTargetParentId
    : undefined;
  const targetFrameId = explicitTargetParentId ? undefined : topLevelFrameIdAtPoint(context.document, viewportCenter, activePageId);
  const sourceBounds = getSceneNodesBounds(payload.nodes);
  const pasteOffset = sourceBounds
    ? {
        x: viewportCenter.x - (sourceBounds.x + sourceBounds.width / 2),
        y: viewportCenter.y - (sourceBounds.y + sourceBounds.height / 2),
      }
    : { x: 0, y: 0 };

  for (const sourceNode of payload.nodes) {
    const parentId = explicitTargetParentId ?? pasteParentIdForNode(sourceNode, activePageId, targetFrameId);
    if (!parentId) {
      continue;
    }

    const siblingCount = childrenCountForParent(context.document, parentId);
    const insertedBefore = insertionCounts.get(parentId) ?? 0;
    const pastedNode = cloneNodeForPaste(sourceNode, context.document, pasteOffset);
    commands.push(new InsertNodeCommand(parentId, pastedNode, siblingCount + insertedBefore));
    pastedNodeIds.push(pastedNode.id);
    insertionCounts.set(parentId, insertedBefore + 1);
  }

  if (commands.length === 0) {
    uiStore.setMessage("No paste target available");
    return;
  }

  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: pastedNodeIds,
    primarySelectedNodeId: pastedNodeIds.at(-1),
    mode: "select",
    selectedVertexId: undefined,
    selectedEdgeIds: [],
    selectedHandle: undefined,
  });
  host.history.execute(
    commands.length === 1 ? commands[0]! : new TransactionCommand("Paste Objects", commands),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  resetDuplicateContinuation();
  host.commit(context.document, pastedNodeIds.length > 1 ? `Pasted ${pastedNodeIds.length} objects` : "Pasted object");
}

export function pasteText(host: EditorCommandHost, characters: string): void {
  const selection = selectionStore.getSelection();
  const context = host.commandContext();
  const activePageId = resolvedActivePageId();
  const viewportCenter = viewStore.viewportCenter.getValue() ?? { x: 0, y: 0 };
  const selectedTargetParentId = selectedPasteContainerId(context.document, selection.selectedNodeIds, selection.primarySelectedNodeId);
  const targetFrameId = selectedTargetParentId ? undefined : topLevelFrameIdAtPoint(context.document, viewportCenter, activePageId);
  const parentId = selectedTargetParentId ?? targetFrameId ?? activePageId;
  if (!parentId) {
    uiStore.setMessage("No paste target available");
    return;
  }

  const siblingCount = childrenCountForParent(context.document, parentId);
  const node = createTextNode(`Text ${siblingCount + 1}`, viewportCenter.x - 100, viewportCenter.y, characters);
  const text = {
    ...node.text,
    textLayout: "AUTO_HEIGHT" as const,
    style: {
      ...node.text.style,
      fontSize: 12,
    },
  };
  const measuredBounds = textBoundsForLayout(
    {
      ...node,
      bounds: { ...node.bounds, width: 200, height: 1 },
      text,
    },
    text,
    Math.round(uiStore.zoom.getValue() * 10),
  );
  node.text = text;
  node.bounds = {
    ...measuredBounds,
    x: viewportCenter.x - 100,
    y: viewportCenter.y - measuredBounds.height / 2,
    width: 200,
  };

  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [node.id],
    primarySelectedNodeId: node.id,
    mode: "select",
    selectedVertexId: undefined,
    selectedEdgeIds: [],
    selectedHandle: undefined,
  });
  host.history.execute(
    new InsertNodeCommand(parentId, node, childrenCountForParent(context.document, parentId)),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  resetDuplicateContinuation();
  host.commit(context.document, "Pasted text");
}

export function pasteSvg(
  host: EditorCommandHost,
  svgMarkup: string,
  insertPoint: { x: number; y: number } | undefined,
): void {
  let result;
  try {
    result = importSvgMarkup(svgMarkup);
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Failed to import SVG");
    return;
  }

  if (result.nodes.length === 0) {
    uiStore.setMessage("SVG did not contain importable content");
    return;
  }

  const selection = selectionStore.getSelection();
  const context = host.commandContext();
  const activePageId = resolvedActivePageId();
  const anchor = insertPoint ?? viewStore.viewportCenter.getValue() ?? { x: 0, y: 0 };
  const selectedTargetParentId = insertPoint
    ? undefined
    : selectedPasteContainerId(context.document, selection.selectedNodeIds, selection.primarySelectedNodeId);
  const targetFrameId = selectedTargetParentId ? undefined : topLevelFrameIdAtPoint(context.document, anchor, activePageId);
  const sourceBounds = getSceneNodesBounds(result.nodes);
  const pasteOffset = sourceBounds
    ? {
        x: anchor.x - (sourceBounds.x + sourceBounds.width / 2),
        y: anchor.y - (sourceBounds.y + sourceBounds.height / 2),
      }
    : { x: 0, y: 0 };

  const commands: InsertNodeCommand[] = [];
  const pastedNodeIds: string[] = [];
  const insertionCounts = new Map<string, number>();

  for (const sourceNode of result.nodes) {
    const parentId = selectedTargetParentId ?? pasteParentIdForNode(sourceNode, activePageId, targetFrameId);
    if (!parentId) {
      continue;
    }
    const siblingCount = childrenCountForParent(context.document, parentId);
    const insertedBefore = insertionCounts.get(parentId) ?? 0;
    const pastedNode = cloneNodeForPaste(sourceNode, context.document, pasteOffset);
    commands.push(new InsertNodeCommand(parentId, pastedNode, siblingCount + insertedBefore));
    pastedNodeIds.push(pastedNode.id);
    insertionCounts.set(parentId, insertedBefore + 1);
  }

  if (commands.length === 0) {
    uiStore.setMessage("No paste target available");
    return;
  }

  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: pastedNodeIds,
    primarySelectedNodeId: pastedNodeIds.at(-1),
    mode: "select",
    selectedVertexId: undefined,
    selectedEdgeIds: [],
    selectedHandle: undefined,
  });
  host.history.execute(
    commands.length === 1 ? commands[0]! : new TransactionCommand("Paste SVG", commands),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  resetDuplicateContinuation();
  host.commit(
    context.document,
    result.issues.length > 0
      ? `Pasted SVG with ${result.issues.length} import note${result.issues.length === 1 ? "" : "s"}`
      : pastedNodeIds.length > 1
        ? `Pasted ${pastedNodeIds.length} SVG objects`
        : "Pasted SVG",
  );
  if (result.issues.length > 0) {
    console.warn("SVG import issues", result.issues);
  }
}

export function pasteImage(
  host: EditorCommandHost,
  imagePaint: Extract<Paint, { type: "image" }>,
  insertPoint: { x: number; y: number } | undefined,
): void {
  const selection = selectionStore.getSelection();
  const context = host.commandContext();
  const editableSelectedIds = editableNodeIds(context.document, selection.selectedNodeIds);
  const nodes = getAllSceneNodes(context.document);
  const selectedNodes = nodes.filter(node => editableSelectedIds.includes(node.id));
  if (selectedNodes.length > 0) {
    const commands = selectedNodes.map(node => new UpdateNodeStyleCommand(
      node.id,
      node.style,
      {
        ...node.style,
        fills: [structuredClone(imagePaint)],
      },
    ));
    const nextSelection = host.currentSelection();
    host.history.execute(
      commands.length === 1 ? commands[0]! : new TransactionCommand("Paste Image Fill", commands),
      context,
      { afterSelection: nextSelection },
    );
    host.commit(context.document, selectedNodes.length > 1 ? `Replaced ${selectedNodes.length} fills` : "Replaced fill");
    return;
  }

  const activePageId = resolvedActivePageId();
  const anchor = insertPoint ?? viewStore.viewportCenter.getValue() ?? { x: 0, y: 0 };
  const selectedTargetParentId = insertPoint
    ? undefined
    : selectedPasteContainerId(context.document, selection.selectedNodeIds, selection.primarySelectedNodeId);
  const targetFrameId = selectedTargetParentId ? undefined : topLevelFrameIdAtPoint(context.document, anchor, activePageId);
  const bounds = imageRectangleBoundsAtPoint(imagePaint, anchor);
  const draftNode = createPrimitiveNode("rectangle", imagePaint.name ?? "Image", bounds.x, bounds.y, bounds.width, bounds.height);
  draftNode.style = {
    ...draftNode.style,
    fills: [structuredClone(imagePaint)],
    strokes: [],
    stroke: undefined,
  };

  const parentId = selectedTargetParentId ?? pasteParentIdForNode(draftNode, activePageId, targetFrameId);
  if (!parentId) {
    uiStore.setMessage("No paste target available");
    return;
  }

  const node = placeNodeInParent(parentId, draftNode, bounds);

  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [node.id],
    primarySelectedNodeId: node.id,
    mode: "select",
    selectedVertexId: undefined,
    selectedEdgeIds: [],
    selectedHandle: undefined,
  });
  host.history.execute(
    new InsertNodeCommand(parentId, node, childrenCountForParent(context.document, parentId)),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  resetDuplicateContinuation();
  host.commit(context.document, "Pasted image");
}

export function dropImageOnCanvas(
  host: EditorCommandHost,
  dataTransfer: DataTransfer,
  point: { x: number; y: number },
): void {
  void readSvgFromDataTransfer(dataTransfer).then(svgMarkup => {
    if (svgMarkup) {
      pasteSvg(host, svgMarkup, point);
      return;
    }
    void readImageClipboardFromDataTransfer(dataTransfer).then(imagePaint => {
      if (!imagePaint) {
        return;
      }
      pasteImage(host, imagePaint, point);
    });
  });
}

export function duplicateSelectedObjects(
  host: EditorCommandHost,
  offset?: { x: number; y: number },
  nodeIds?: string[],
  label = "Duplicate Selection",
  dropPoint?: { x: number; y: number },
): void {
  const selection = selectionStore.getSelection();
  const targetNodeIds = nodeIds ?? selection.selectedNodeIds;
  if (selection.mode === "textEdit" || targetNodeIds.length === 0) {
    return;
  }

  const context = host.commandContext();
  const activePageId = resolvedActivePageId();
  const duplicateNodeIds = editableNodeIds(context.document, targetNodeIds);
  const continuesDuplicateChain = clipboardStore.duplicateContinuationSourceNodeIds === undefined
    || stringArraysEqual(duplicateNodeIds, clipboardStore.duplicateContinuationSourceNodeIds);
  const duplicateOffset = duplicateOffsetForSelection(offset, clipboardStore.lastDuplicateOffset, continuesDuplicateChain);
  const duplicated = createDuplicateNodeCommands(context.document, duplicateNodeIds, duplicateOffset);
  if (duplicated.commands.length === 0) {
    uiStore.setMessage("Nothing to duplicate");
    return;
  }
  const frameDropTarget = dropPoint ? frameDropTargetForMovedNodes(context.document, duplicateNodeIds, dropPoint, activePageId) : undefined;
  const commands: Command[] = frameDropTarget
    ? [...duplicated.commands, new MoveNodesInTreeCommand(duplicated.nodeIds, frameDropTarget)]
    : duplicated.commands;

  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: duplicated.nodeIds,
    primarySelectedNodeId: duplicated.nodeIds.at(-1),
    mode: "select",
    selectedVertexId: undefined,
    selectedEdgeIds: [],
    selectedHandle: undefined,
  });
  host.history.execute(
    commands.length === 1 ? commands[0]! : new TransactionCommand(label, commands),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  clipboardStore.lastDuplicateOffset = offset ?? (continuesDuplicateChain ? duplicateOffset : DEFAULT_DUPLICATE_OFFSET);
  clipboardStore.duplicateContinuationSourceNodeIds = duplicated.nodeIds;
  host.commit(context.document, duplicated.nodeIds.length > 1 ? `Duplicated ${duplicated.nodeIds.length} objects` : "Duplicated object");
}

export async function copySelectedAnimationNodes(): Promise<void> {
  const selection = selectionStore.getSelection();
  const animationGraph = currentAnimationGraph();
  if (!animationGraph || (selection.selectedAnimationNodeIds ?? []).length === 0) {
    return;
  }
  const nodes = animationGraph.nodes.filter(n => (selection.selectedAnimationNodeIds ?? []).includes(n.id));
  if (nodes.length === 0) {
    return;
  }
  const nodeIdSet = new Set(nodes.map(n => n.id));
  const connections = animationGraph.connections.filter(
    c => nodeIdSet.has(c.from.nodeId) && nodeIdSet.has(c.to.nodeId),
  );
  const clonedNodes = structuredClone(nodes);
  const clonedConnections = structuredClone(connections);
  // Keep the in-memory store as a synchronous fast path / fallback for shells
  // without the async clipboard API.
  clipboardStore.animationClipboard = { nodes: clonedNodes, connections: clonedConnections };

  const payload: CurvaAnimationClipboardPayload = {
    format: "curva.animation-nodes",
    version: 1,
    nodes: clonedNodes,
    connections: clonedConnections,
  };
  try {
    await writeAnimationClipboard(payload);
    uiStore.setMessage(nodes.length > 1 ? `Copied ${nodes.length} animation nodes` : "Copied animation node");
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Copy failed");
  }
}

/** Rewrite cross-references between animation nodes to the freshly-generated ids. */
function remapAnimationNodeReferences(node: AnimationGraphNode, idMap: Map<string, string>): AnimationGraphNode {
  const remapTarget = <T extends AnimationTarget | undefined>(target: T): T => {
    if (target && target.kind === "animationNode") {
      return { ...target, nodeId: idMap.get(target.nodeId) ?? target.nodeId } as T;
    }
    return target;
  };

  const clone = structuredClone(node);
  if (clone.kind === "motion" && clone.targetAnimationNodeId) {
    clone.targetAnimationNodeId = idMap.get(clone.targetAnimationNodeId) ?? clone.targetAnimationNodeId;
  }
  if (clone.kind === "group") {
    clone.targets = clone.targets.map(target => remapTarget(target));
  }
  if (clone.kind === "animate") {
    clone.target = remapTarget(clone.target);
    clone.properties = clone.properties.map(property => {
      // Only these property fields can reference another animation node; `pin.target`
      // is an AnimationPinTarget (frame anchor / distance / offset), not a node ref.
      if (property.kind === "pin") {
        return { ...property, dependencyTarget: remapTarget(property.dependencyTarget) };
      }
      if (property.kind === "followPath") {
        return { ...property, pathTarget: remapTarget(property.pathTarget) };
      }
      if (property.kind === "orbit" || property.kind === "lookAt" || property.kind === "fitToBBox") {
        return { ...property, target: remapTarget(property.target) };
      }
      return property;
    });
  }
  return clone;
}

function insertPastedAnimationNodes(
  host: EditorCommandHost,
  clipboard: { nodes: AnimationGraphNode[]; connections: AnimationConnection[] },
): void {
  if (clipboard.nodes.length === 0) {
    return;
  }
  const PASTE_OFFSET = { x: 20, y: 20 };
  const idMap = new Map<string, string>();
  for (const node of clipboard.nodes) {
    idMap.set(node.id, createId("animNode"));
  }
  const newNodes: AnimationGraphNode[] = clipboard.nodes.map(node => {
    const remapped = remapAnimationNodeReferences(node, idMap);
    return {
      ...remapped,
      id: idMap.get(node.id)!,
      position: { x: node.position.x + PASTE_OFFSET.x, y: node.position.y + PASTE_OFFSET.y },
    };
  });
  const newConnections: AnimationConnection[] = clipboard.connections.map(conn => ({
    ...structuredClone(conn),
    id: createId("animConn"),
    from: { ...conn.from, nodeId: idMap.get(conn.from.nodeId) ?? conn.from.nodeId },
    to: { ...conn.to, nodeId: idMap.get(conn.to.nodeId) ?? conn.to.nodeId },
  }));
  const context = host.commandContext();
  const commands: Command[] = [
    ...newNodes.map(node => new InsertAnimationNodeCommand(node)),
    ...newConnections.map(conn => new UpsertAnimationConnectionCommand(conn)),
  ];
  const newNodeIds = newNodes.map(n => n.id);
  const nextSelection = host.selectionSnapshot({ selectedAnimationNodeIds: newNodeIds, primarySelectedAnimationNodeId: newNodeIds.at(-1) });
  host.history.execute(
    commands.length === 1 ? commands[0]! : new TransactionCommand("Paste animation nodes", commands),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, newNodes.length > 1 ? `Pasted ${newNodes.length} animation nodes` : "Pasted animation node");
  clipboardStore.animationClipboard = {
    nodes: clipboard.nodes.map(n => ({ ...structuredClone(n), position: { x: n.position.x + PASTE_OFFSET.x, y: n.position.y + PASTE_OFFSET.y } })),
    connections: clipboard.connections.map(c => structuredClone(c)),
  };
}

export async function pasteAnimationNodes(host: EditorCommandHost): Promise<void> {
  if (!currentAnimationGraph()) {
    return;
  }
  const systemPayload = await readAnimationClipboard();
  const source = systemPayload
    ? { nodes: systemPayload.nodes, connections: systemPayload.connections }
    : clipboardStore.animationClipboard;
  if (!source || source.nodes.length === 0) {
    uiStore.setMessage("No animation nodes to paste");
    return;
  }
  // Validate/normalize incoming data (drops dangling connections, coerces fields).
  const normalized = normalizeAnimationGraph({ nodes: source.nodes, connections: source.connections });
  insertPastedAnimationNodes(host, { nodes: normalized.nodes, connections: normalized.connections });
}

export function deleteNodes(host: EditorCommandHost, nodeIds: string[]): void {
  const nodes = getAllSceneNodes(documentStore.getDocument()).filter(node => nodeIds.includes(node.id) && !isNodeLockedForEditing(documentStore.getDocument(), node.id));
  if (nodes.length === 0) {
    return;
  }

  const context = host.commandContext();
  const commands = nodes.map(node => new DeleteNodeCommand(node.id));
  const nextSelection = host.selectionSnapshot({ selectedNodeIds: [], primarySelectedNodeId: undefined, mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined });
  host.history.execute(
    commands.length === 1 ? commands[0]! : new TransactionCommand(`Delete ${commands.length} nodes`, commands),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, nodes.length > 1 ? `Deleted ${nodes.length} nodes` : `Deleted ${nodes[0]!.name}`);
}

function vectorSelectionPayload(document: CurvaDocument, nodeId: string, vertexIds: string[]): VectorSelectionClipboard | undefined {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork") {
    return undefined;
  }

  const selectedIds = new Set(vertexIds);
  const vertices = node.network.vertices.filter(vertex => selectedIds.has(vertex.id)).map(vertex => structuredClone(vertex));
  if (vertices.length === 0) {
    return undefined;
  }

  const copiedVertexIds = new Set(vertices.map(vertex => vertex.id));
  const edges = node.network.edges
    .filter(edge => copiedVertexIds.has(edge.from.vertexId) && copiedVertexIds.has(edge.to.vertexId))
    .map(edge => structuredClone(edge));
  const copiedEdgeIds = new Set(edges.map(edge => edge.id));
  const regions = node.network.regions
    .filter(region => region.edgeIds.length > 0 && region.edgeIds.every(edgeId => copiedEdgeIds.has(edgeId)))
    .map(region => structuredClone(region));
  return {
    nodeId,
    vertices,
    edges,
    regions,
    bounds: boundsFromVectorVertices(vertices),
  };
}

function vectorSelectionPayloadToNode(payload: VectorSelectionClipboard, offset: { x: number; y: number }, style?: NodeStyle): VectorNetworkNode {
  const vertexIdMap = new Map(payload.vertices.map(vertex => [vertex.id, createId("vertex")]));
  const edgeIdMap = new Map(payload.edges.map(edge => [edge.id, createId("edge")]));
  const vertices = payload.vertices.map(vertex => ({
    ...structuredClone(vertex),
    id: vertexIdMap.get(vertex.id)!,
    x: vertex.x + offset.x,
    y: vertex.y + offset.y,
  }));
  const edges = payload.edges.flatMap(edge => {
    const fromVertexId = vertexIdMap.get(edge.from.vertexId);
    const toVertexId = vertexIdMap.get(edge.to.vertexId);
    const edgeId = edgeIdMap.get(edge.id);
    return fromVertexId && toVertexId && edgeId
      ? [{
          ...structuredClone(edge),
          id: edgeId,
          from: {
            ...structuredClone(edge.from),
            vertexId: fromVertexId,
            handle: edge.from.handle ? offsetPoint(edge.from.handle, offset) : undefined,
          },
          to: {
            ...structuredClone(edge.to),
            vertexId: toVertexId,
            handle: edge.to.handle ? offsetPoint(edge.to.handle, offset) : undefined,
          },
        }]
      : [];
  });
  const regions = payload.regions.flatMap(region => {
    const edgeIds = region.edgeIds.map(edgeId => edgeIdMap.get(edgeId));
    return edgeIds.every(Boolean)
      ? [{ ...structuredClone(region), id: createId("region"), edgeIds: edgeIds as string[] }]
      : [];
  });
  const network = {
    vertices,
    edges,
    regions,
    fillRule: "nonzero" as const,
  };

  return {
    id: createId("node"),
    name: payload.vertices.length > 1 ? "Vector Selection" : "Vector Vertex",
    type: "vectorNetwork",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: getVectorNetworkBounds(network),
    style: style ? structuredClone(style) : createDefaultStyle("#38bdf8"),
    network,
  };
}

function topLevelFrameIdAtPoint(document: CurvaDocument, point: { x: number; y: number }, activePageId?: string): string | undefined {
  const page = getActivePage(document, activePageId);
  if (!page) {
    return undefined;
  }

  const frame = [...page.children]
    .reverse()
    .find(node => node.type === "frame" && node.visible && !isNodeLockedForEditing(document, node.id) && pointInRect(point, node.bounds));
  return frame?.id;
}

function nodeContainsId(node: SceneNode, nodeId: string): boolean {
  if (node.id === nodeId) {
    return true;
  }

  const children = getSceneNodeChildren(node);
  return children ? children.some(child => nodeContainsId(child, nodeId)) : false;
}

function getSceneNodesBounds(nodes: SceneNode[]): Rect | undefined {
  if (nodes.length === 0) {
    return undefined;
  }

  const left = Math.min(...nodes.map(node => node.bounds.x));
  const top = Math.min(...nodes.map(node => node.bounds.y));
  const right = Math.max(...nodes.map(node => node.bounds.x + node.bounds.width));
  const bottom = Math.max(...nodes.map(node => node.bounds.y + node.bounds.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function boundsFromVectorVertices(vertices: VectorVertex[]): Rect {
  let minX = vertices[0]?.x ?? 0;
  let minY = vertices[0]?.y ?? 0;
  let maxX = minX;
  let maxY = minY;
  for (const vertex of vertices.slice(1)) {
    minX = Math.min(minX, vertex.x);
    minY = Math.min(minY, vertex.y);
    maxX = Math.max(maxX, vertex.x);
    maxY = Math.max(maxY, vertex.y);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function offsetPoint(point: { x: number; y: number }, offset: { x: number; y: number }): { x: number; y: number } {
  return {
    x: point.x + offset.x,
    y: point.y + offset.y,
  };
}

function pointInRect(point: { x: number; y: number }, rect: Rect): boolean {
  const x1 = Math.min(rect.x, rect.x + rect.width);
  const x2 = Math.max(rect.x, rect.x + rect.width);
  const y1 = Math.min(rect.y, rect.y + rect.height);
  const y2 = Math.max(rect.y, rect.y + rect.height);
  return point.x >= x1 && point.x <= x2 && point.y >= y1 && point.y <= y2;
}

function rectsIntersect(left: Rect, right: Rect): boolean {
  const leftX1 = Math.min(left.x, left.x + left.width);
  const leftX2 = Math.max(left.x, left.x + left.width);
  const leftY1 = Math.min(left.y, left.y + left.height);
  const leftY2 = Math.max(left.y, left.y + left.height);
  const rightX1 = Math.min(right.x, right.x + right.width);
  const rightX2 = Math.max(right.x, right.x + right.width);
  const rightY1 = Math.min(right.y, right.y + right.height);
  const rightY2 = Math.max(right.y, right.y + right.height);
  return leftX1 <= rightX2 && leftX2 >= rightX1 && leftY1 <= rightY2 && leftY2 >= rightY1;
}

function vectorEditLocalPointForNode(document: CurvaDocument, nodeId: string, point: { x: number; y: number }): { x: number; y: number } {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork" || node.transform.rotation === 0) {
    return point;
  }

  return inverseRotatePoint(point, transformRotationOrigin(node.bounds, node.transform), node.transform.rotation);
}

function stringArraysEqual(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function styleCopyMessage(payload: CurvaStyleClipboardPayload, nodeName: string): string {
  const copied = payload.parts.filter(part => part !== "opacity" && part !== "cornerRadii");
  if (payload.parts.includes("opacity") || payload.parts.includes("cornerRadii")) {
    return `Copied style from ${nodeName}`;
  }

  if (copied.length === 1) {
    return `Copied ${copied[0]} from ${nodeName}`;
  }

  return `Copied style from ${nodeName}`;
}
